import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Download, Filter, Calendar, Users, FileText, DollarSign, 
  AlertCircle, CheckCircle, Clock, BarChart3
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Label, Select } from '../../components/ui/Form';
import { Badge } from '../../components/ui/Badge';
import { useToast } from '../../contexts/ToastContext';
import { schoolsAPI, reportsAPI, lookupDataAPI } from '../../services/api';

const Reports = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('meal-requests');
  const [schools, setSchools] = useState([]);
  const [users, setUsers] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [filters, setFilters] = useState({
    schoolId: '',
    startDate: '',
    endDate: '',
    userId: ''
  });

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [schoolsData, usersData] = await Promise.all([
        schoolsAPI.getSchools(),
        lookupDataAPI.getUsers()
      ]);
      
      console.log('Schools API Response:', schoolsData);
      console.log('Users API Response:', usersData);
      
      // Handle different response structures for schools
      let schoolsArray = [];
      if (Array.isArray(schoolsData)) {
        schoolsArray = schoolsData;
      } else if (schoolsData.data && Array.isArray(schoolsData.data)) {
        schoolsArray = schoolsData.data;
      } else if (schoolsData.schools && Array.isArray(schoolsData.schools)) {
        schoolsArray = schoolsData.schools;
      }
      
      // Handle different response structures for users
      let usersArray = [];
      if (Array.isArray(usersData)) {
        usersArray = usersData;
      } else if (usersData.users && Array.isArray(usersData.users)) {
        usersArray = usersData.users;
      } else if (usersData.data && Array.isArray(usersData.data)) {
        usersArray = usersData.data;
      }
      
      console.log('Processed Schools:', schoolsArray);
      console.log('Processed Users:', usersArray);
      
      setSchools(schoolsArray);
      setUsers(usersArray);
    } catch (error) {
      console.error('Failed to load initial data:', error);
      toast.error('Failed to load data', 'Please try again');
    }
  };

  // Meal Requests Report
  const loadMealRequestsReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.schoolId) params.school_id = filters.schoolId;
      if (filters.startDate) params.start_date = filters.startDate;
      if (filters.endDate) params.end_date = filters.endDate;
      
      const data = await reportsAPI.getMealRequests(params);
      setReportData(data);
      toast.success('Meal requests report generated successfully');
    } catch (error) {
      toast.error('Failed to generate meal requests report', error.message);
    } finally {
      setLoading(false);
    }
  };

  // School Invoice Report
  const loadSchoolInvoiceReport = async () => {
    if (!filters.schoolId) {
      toast.error('Please select a school for invoice report');
      return;
    }
    
    if (!filters.startDate) {
      toast.error('Please select a month for invoice report');
      return;
    }

    setLoading(true);
    try {
      // Convert date to YYYY-MM format for invoice
      const month = filters.startDate.substring(0, 7); // YYYY-MM
      const data = await reportsAPI.getSchoolInvoice(filters.schoolId, month);
      setReportData(data);
      toast.success('School invoice generated successfully');
    } catch (error) {
      toast.error('Failed to generate school invoice', error.message);
    } finally {
      setLoading(false);
    }
  };

  // Employee Attendance Report
  const loadEmployeeAttendanceReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.startDate) params.start_date = filters.startDate;
      if (filters.endDate) params.end_date = filters.endDate;
      if (filters.userId) params.user_id = filters.userId;
      
      const data = await reportsAPI.getEmployeeAttendance(params);
      setReportData(data);
      toast.success('Employee attendance report generated successfully');
    } catch (error) {
      toast.error('Failed to generate employee attendance report', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const generateReport = () => {
    switch (activeTab) {
      case 'meal-requests':
        loadMealRequestsReport();
        break;
      case 'school-invoice':
        loadSchoolInvoiceReport();
        break;
      case 'employee-attendance':
        loadEmployeeAttendanceReport();
        break;
      default:
        toast.error('Please select a report type');
    }
  };

  const downloadReport = async () => {
    if (!reportData) {
      toast.error('Please generate a report first');
      return;
    }

    try {
      let blob;
      let filename;
      const params = {};
      if (filters.schoolId) params.school_id = filters.schoolId;
      if (filters.startDate) params.start_date = filters.startDate;
      if (filters.endDate) params.end_date = filters.endDate;

      switch (activeTab) {
        case 'meal-requests':
          blob = await reportsAPI.exportMealRequestsPDF(params);
          filename = 'meal_requests_report.pdf';
          break;
        case 'school-invoice':
          if (!filters.schoolId || !filters.startDate) {
            toast.error('School and month are required for invoice download');
            return;
          }
          const month = filters.startDate.substring(0, 7);
          blob = await reportsAPI.exportSchoolInvoicePDF(filters.schoolId, month);
          filename = `school_invoice_${filters.schoolId}_${month}.pdf`;
          break;
        case 'employee-attendance':
          blob = await reportsAPI.exportEmployeeAttendanceExcel(params);
          filename = 'employee_attendance_report.xlsx';
          break;
        default:
          toast.error('Report download not available for this type');
          return;
      }

      // Download file
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded successfully');
    } catch (error) {
      toast.error('Failed to download report', error.message);
    }
  };

  const tabs = [
    { id: 'meal-requests', label: 'Meal Requests', icon: FileText },
    { id: 'school-invoice', label: 'School Invoice', icon: DollarSign },
    { id: 'employee-attendance', label: 'Employee Attendance', icon: Clock }
  ];

  const getStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-700';
    switch (status) {
      case 'delivered': return 'bg-green-100 text-green-700';
      case 'acknowledged': return 'bg-blue-100 text-blue-700';
      case 'preparing': return 'bg-yellow-100 text-yellow-700';
      case 'ready': return 'bg-purple-100 text-purple-700';
      case 'pending': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Reports</h1>
            <p className="text-gray-600 mt-2">Generate reports for meal requests, school invoices, and employee attendance</p>
          </div>
        </div>
      </motion.div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon size={18} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Filters Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Card>
          <CardHeader>
            <CardTitle>Report Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              {(activeTab === 'meal-requests' || activeTab === 'school-invoice') && (
                <div>
                  <Label htmlFor="schoolId">School</Label>
                  <Select
                    value={filters.schoolId}
                    onChange={(e) => handleFilterChange('schoolId', e.target.value)}
                  >
                    <option value="">All Schools</option>
                    {schools.map(school => (
                      <option key={school.id} value={school.id}>
                        {school.school_name || school.name || school.branch_name || `School ${school.id}`}
                      </option>
                    ))}
                  </Select>
                </div>
              )}

              {activeTab === 'employee-attendance' && (
                <div>
                  <Label htmlFor="userId">Employee</Label>
                  <Select
                    value={filters.userId}
                    onChange={(e) => handleFilterChange('userId', e.target.value)}
                  >
                    <option value="">All Employees</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.full_name}
                      </option>
                    ))}
                  </Select>
                </div>
              )}

              <div>
                <Label htmlFor="startDate">
                  {activeTab === 'school-invoice' ? 'Month' : 'Start Date'}
                </Label>
                <Input
                  type={activeTab === 'school-invoice' ? 'month' : 'date'}
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                />
              </div>

              {activeTab !== 'school-invoice' && (
                <div>
                  <Label htmlFor="endDate">End Date</Label>
                  <Input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  />
                </div>
              )}
            </div>

            <div className="flex space-x-3">
              <Button
                onClick={generateReport}
                loading={loading}
                className="flex items-center space-x-2"
              >
                <Filter size={18} />
                <span>Generate Report</span>
              </Button>
              
              {reportData && (
                <Button
                  variant="outline"
                  onClick={downloadReport}
                  className="flex items-center space-x-2"
                >
                  <Download size={18} />
                  <span>Download Report</span>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Report Results */}
      {reportData && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {/* Meal Requests Report */}
          {activeTab === 'meal-requests' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center">
                      <FileText className="text-blue-600 mr-2" size={24} />
                      <div>
                        <p className="text-sm text-gray-600">Total Requests</p>
                        <p className="text-2xl font-bold text-gray-900">{reportData.total_requests || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center">
                      <Users className="text-green-600 mr-2" size={24} />
                      <div>
                        <p className="text-sm text-gray-600">PG-5 Students</p>
                        <p className="text-2xl font-bold text-gray-900">{reportData.total_pg_5_students || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center">
                      <Users className="text-purple-600 mr-2" size={24} />
                      <div>
                        <p className="text-sm text-gray-600">5-10 Students</p>
                        <p className="text-2xl font-bold text-gray-900">{reportData.total_five_10_students || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center">
                      <CheckCircle className="text-orange-600 mr-2" size={24} />
                      <div>
                        <p className="text-sm text-gray-600">Total Meals</p>
                        <p className="text-2xl font-bold text-gray-900">{reportData.total_meals || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {reportData.records && reportData.records.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Meal Requests</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">School</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PG-5</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">5-10</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {reportData.records.slice(0, 10).map((record) => (
                            <tr key={record.id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">{record.date}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">{record.school_name || 'Unknown School'}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">{record.pg_5_students || 0}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">{record.five_10_students || 0}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{record.total_meals || 0}</td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <Badge className={getStatusColor(record.status)}>
                                  {record.status ? record.status.replace('_', ' ').replace('-', ' ').toUpperCase() : 'UNKNOWN'}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* School Invoice Report */}
          {activeTab === 'school-invoice' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Invoice: {reportData.invoice_number}</CardTitle>
                  <p className="text-sm text-gray-600">{reportData.school_name}</p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="text-sm text-blue-600">Total Days</p>
                      <p className="text-2xl font-bold text-blue-800">{reportData.summary?.total_days || 0}</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="text-sm text-green-600">Total Meals</p>
                      <p className="text-2xl font-bold text-green-800">{reportData.summary?.total_meals || 0}</p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <p className="text-sm text-purple-600">PG-5 Students</p>
                      <p className="text-2xl font-bold text-purple-800">{reportData.summary?.total_pg_5_students || 0}</p>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <p className="text-sm text-orange-600">5-10 Students</p>
                      <p className="text-2xl font-bold text-orange-800">{reportData.summary?.total_five_10_students || 0}</p>
                    </div>
                  </div>

                  <h4 className="font-semibold mb-3">Invoice Breakdown</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Students</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate (NPR)</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount (NPR)</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">PG-5 Students</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">{reportData.summary?.total_pg_5_students || 0}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">{reportData.pricing?.price_per_meal_pg_5 || 0}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{reportData.calculations?.subtotal_pg_5 || 0}</td>
                        </tr>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">5-10 Students</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">{reportData.summary?.total_five_10_students || 0}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">{reportData.pricing?.price_per_meal_five_10 || 0}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{reportData.calculations?.subtotal_five_10 || 0}</td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold">TOTAL</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">-</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">-</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-lg">{reportData.calculations?.total_amount || 0}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Employee Attendance Report */}
          {activeTab === 'employee-attendance' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center">
                      <Users className="text-blue-600 mr-2" size={24} />
                      <div>
                        <p className="text-sm text-gray-600">Total Employees</p>
                        <p className="text-2xl font-bold text-gray-900">{reportData.total_employees || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center">
                      <Calendar className="text-green-600 mr-2" size={24} />
                      <div>
                        <p className="text-sm text-gray-600">Working Days</p>
                        <p className="text-2xl font-bold text-gray-900">{reportData.total_working_days || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center">
                      <Clock className="text-orange-600 mr-2" size={24} />
                      <div>
                        <p className="text-sm text-gray-600">Late Arrivals</p>
                        <p className="text-2xl font-bold text-gray-900">{reportData.total_late_arrivals || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center">
                      <AlertCircle className="text-red-600 mr-2" size={24} />
                      <div>
                        <p className="text-sm text-gray-600">Early Departures</p>
                        <p className="text-2xl font-bold text-gray-900">{reportData.total_early_departures || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {reportData.records && reportData.records.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Attendance Records</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clock In</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clock Out</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Late (min)</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Early (min)</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {reportData.records.slice(0, 10).map((record) => (
                            <tr key={record.id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{record.employee_name || 'Unknown'}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">{record.date}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {record.clock_in_time ? new Date(record.clock_in_time).toLocaleTimeString() : 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {record.clock_out_time ? new Date(record.clock_out_time).toLocaleTimeString() : 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">{record.late_minutes || 0}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">{record.early_departure_minutes || 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default Reports;
